/**
 * Day View — mirrors iOS DayView layout and look (weather header, typed item cards,
 * options, notes, sticky Add Item / Expand footer).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Spin, message } from 'antd';
import { ArrowLeftOutlined, DownOutlined, UpOutlined, PlusOutlined, EditOutlined, FileOutlined, PictureOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessibleTrip, updateTrip } from '../../services/tripsService';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import type { ItineraryItem, ItineraryItemType, Trip, TripCityOption } from '../../types/client';
import type { ForecastDay, HourlyForecast } from '../../types/weather';
import { dayViewEn, tripDetailEn, tourSearchEn } from '../../content/trips.en';
import { weatherEn } from '../../content/weather.en';
import {
  isHourlyStripVisible,
  loadWeatherForCityDay,
  type WeatherLoadStatus,
} from '../../services/weatherOrchestrator';
import { detectWeatherAlerts } from '../../services/weatherAlertService';
import { formatTempRange, resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import { weatherHeroVariant } from '../../utils/weatherHero';
import WeatherConditionIcon from '../../components/weather/WeatherConditionIcon';
import DayWeatherSummary from '../../components/weather/DayWeatherSummary';
import HourlyWeatherStrip from '../../components/weather/HourlyWeatherStrip';
import WeatherAlertsDrawer from '../../components/weather/WeatherAlertsDrawer';
import CountryInfoDrawer from '../../components/CountryInfo/CountryInfoDrawer';
import { isAtSeaLocation } from '../../services/cruiseItinerariesApi';
import {
  formatCardWeekdayDate,
  formatPortClockHm,
  portTimeLineForCard,
  buildTripDayCards,
} from '../../utils/tripDayCards';
import { isItemStillUpcoming } from '../../utils/preferenceFilters';
import { mapsSearchUrl } from '../../utils/preferredMapUrl';
import PlaceSearchModal, {
  type PlaceSearchCity,
  type PlaceSearchSelection,
  type PlaceSearchTourAdd,
} from '../../components/PlaceSearchModal/PlaceSearchModal';
import AddDayItemModal, {
  prefillFromPlace,
  prefillFromItem,
  type AddDayItemPrefill,
  type AddDayItemSavePayload,
} from '../../components/AddDayItemModal/AddDayItemModal';
import NotesAttachmentsCard from '../../components/NotesAttachmentsCard/NotesAttachmentsCard';
import {
  AccommodationIcon,
  CheckIcon,
  ClockIcon,
  FerryIcon,
  GlobeIcon,
  MapIcon,
  PhoneIcon,
  PinIcon,
  TypeIcon,
} from '../../components/icons/TripIcons';
import { downloadFile } from '../../services/filesService';
import {
  applyItemAttachmentsOnSave,
  fileIdForAttachment,
  removeAttachment,
  visibleDayItems,
} from '../../utils/notesAttachments';
import { formatDurationMinutes } from '../../utils/durationUnits';
import { buildTourItineraryItem } from '../../utils/tourItemSave';
import styles from './DayView.module.css';

const TYPE_ORDER: ItineraryItemType[] = [
  'meal',
  'activity',
  'transport',
  'accommodation',
  'note',
  'other',
];

const STATUS_LABEL: Record<string, string> = {
  docked: tripDetailEn.docked,
  tendered: tripDetailEn.tendered,
  cruising: tripDetailEn.cruising,
  unknown: dayViewEn.unknown,
};

function sortItems(items: ItineraryItem[]): ItineraryItem[] {
  return [...items].sort((a, b) => {
    const ta = a.time ?? '';
    const tb = b.time ?? '';
    if (ta !== tb) return ta.localeCompare(tb);
    return TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
  });
}

const DayView: React.FC = () => {
  const { tripId, date } = useParams<{ tripId: string; date: string }>();
  const [searchParams] = useSearchParams();
  const cityVisitId = searchParams.get('cityVisitId');
  const navigate = useNavigate();
  const { user, isDataSyncing } = useAuth();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [expandAll, setExpandAll] = useState(() => {
    const prefs = loadLocalUserPreferences().appUserSettings;
    return prefs.expandAllItemsByDefault !== false;
  });
  const [collapsePastItems, setCollapsePastItems] = useState(
    () => loadLocalUserPreferences().appUserSettings.collapsePastItems
  );
  const [hideCompletedOptions, setHideCompletedOptions] = useState(
    () => loadLocalUserPreferences().appUserSettings.hideCompletedOptions
  );
  const [preferredMapApp, setPreferredMapApp] = useState(
    () => loadLocalUserPreferences().appUserSettings.preferredMapApp
  );
  const [placeSearchOpen, setPlaceSearchOpen] = useState(false);
  const [tourAdding, setTourAdding] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemPrefill, setAddItemPrefill] = useState<AddDayItemPrefill | null>(null);
  const [itemModalMode, setItemModalMode] = useState<'add' | 'edit'>('add');
  const [editingItem, setEditingItem] = useState<ItineraryItem | null>(null);
  const [weatherDay, setWeatherDay] = useState<ForecastDay | null>(null);
  const [weatherHourly, setWeatherHourly] = useState<HourlyForecast[]>([]);
  const [weatherStatus, setWeatherStatus] = useState<WeatherLoadStatus>('loading');
  const [weatherLastUpdated, setWeatherLastUpdated] = useState<string | undefined>();
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false);
  const [countryInfoOpen, setCountryInfoOpen] = useState(false);
  /** Reserved for Google Places photo URL — wire when Places integration is added. */
  const [cityPhotoUrl] = useState<string | null>(null);

  const reload = useCallback((): void => {
    if (!user?.sub || !tripId) {
      setTrip(null);
      setLoading(false);
      return;
    }
    setTrip(getAccessibleTrip(user.sub, tripId));
    const prefs = loadLocalUserPreferences().appUserSettings;
    setExpandAll(prefs.expandAllItemsByDefault !== false);
    setCollapsePastItems(prefs.collapsePastItems);
    setHideCompletedOptions(prefs.hideCompletedOptions);
    setPreferredMapApp(prefs.preferredMapApp);
    setLoading(false);
  }, [user?.sub, tripId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [date, cityVisitId]);

  useEffect(() => {
    if (!isDataSyncing) reload();
  }, [isDataSyncing, reload]);

  const dayCards = useMemo(() => (trip ? buildTripDayCards(trip) : []), [trip]);

  const focusCard = useMemo(() => {
    if (!dayCards.length) return null;
    if (cityVisitId && date) {
      const exact = dayCards.find((c) => c.cityId === cityVisitId && c.arrivalDate === date);
      if (exact) return exact;
    }
    if (date) {
      const byDate = dayCards.find((c) => c.arrivalDate === date);
      if (byDate) return byDate;
    }
    if (cityVisitId) {
      const byId = dayCards.find((c) => c.cityId === cityVisitId);
      if (byId) return byId;
    }
    return dayCards[0];
  }, [dayCards, cityVisitId, date]);

  const dayDate = date || focusCard?.arrivalDate || null;

  const dayItems = useMemo(() => {
    if (!trip || !dayDate) return [];
    const day = (trip.itineraryDays ?? []).find((d) => d.date === dayDate);
    const items = visibleDayItems(day?.items ?? []).filter(
      (i) => i.type !== 'accommodation' // lodging shown separately as hotel/tonight on iOS
    );
    return sortItems(items);
  }, [trip, dayDate]);

  const accommodations = useMemo(() => {
    if (!trip || !dayDate) return [];
    const day = (trip.itineraryDays ?? []).find((d) => d.date === dayDate);
    return visibleDayItems(day?.items ?? []).filter((i) => i.type === 'accommodation');
  }, [trip, dayDate]);

  const dayItemsAll = useMemo(() => {
    if (!trip || !dayDate) return [];
    return (trip.itineraryDays ?? []).find((d) => d.date === dayDate)?.items ?? [];
  }, [trip, dayDate]);

  const options: TripCityOption[] = useMemo(() => {
    if (!trip || !focusCard) return [];
    const all = trip.cityOptions?.[focusCard.cityId] ?? [];
    if (!hideCompletedOptions) return all;
    return all.filter((o) => !o.completed);
  }, [trip, focusCard, hideCompletedOptions]);

  /** Place search is locked to the day being viewed — no other-city picker. */
  const searchCities: PlaceSearchCity[] = useMemo(() => {
    if (!trip || !focusCard) return [];
    if (isAtSeaLocation(focusCard.cityName)) return [];
    const coords = trip.cityCoords?.[focusCard.cityId];
    return [
      {
        id: focusCard.cityId,
        cityName: focusCard.cityName,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      },
    ];
  }, [trip, focusCard]);

  const navTitle = useMemo(() => {
    if (!dayDate) return dayViewEn.untitledDay;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayDate);
    if (!match) return dayDate;
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, [dayDate]);

  const weekdayLabel = formatCardWeekdayDate(dayDate);
  const cityName = focusCard?.cityName ?? dayViewEn.unknownCity;
  const atSea = isAtSeaLocation(cityName);
  const timeLine = focusCard ? portTimeLineForCard(focusCard) : null;

  const statusLabel =
    focusCard?.isCruisePort && focusCard.cruisePortStatus
      ? STATUS_LABEL[focusCard.cruisePortStatus] ?? tripDetailEn.docked
      : focusCard?.isCruisePort
        ? tripDetailEn.docked
        : null;

  const temperatureUnit = trip ? resolveTripTemperatureUnit(trip) : 'F';

  useEffect(() => {
    if (!trip || !user?.sub || !dayDate || !focusCard?.cityId || atSea) {
      setWeatherDay(null);
      setWeatherHourly([]);
      setWeatherStatus(atSea ? 'unavailable' : 'unavailable');
      return;
    }

    const cityId = focusCard.cityId;
    const coords = trip.cityCoords?.[cityId];
    let cancelled = false;

    setWeatherStatus('loading');
    setWeatherDay(null);
    setWeatherHourly([]);

    void loadWeatherForCityDay({
      userId: user.sub,
      cityVisitId: cityId,
      lat: coords?.latitude,
      lng: coords?.longitude,
      date: dayDate,
    }).then((result) => {
      if (cancelled) return;
      setWeatherDay(result.day);
      setWeatherHourly(result.hourly);
      setWeatherStatus(result.status);
      setWeatherLastUpdated(result.lastUpdated);
    });

    return () => {
      cancelled = true;
    };
  }, [trip, user?.sub, dayDate, focusCard?.cityId, atSea]);

  const weatherAlerts = useMemo(
    () => detectWeatherAlerts(weatherDay),
    [weatherDay]
  );

  const showHourlyStrip = Boolean(dayDate && isHourlyStripVisible(dayDate) && weatherHourly.length > 0);

  const heroVariant = weatherDay
    ? weatherHeroVariant(weatherDay.condition, weatherDay.iconName)
    : 'heroDefault';

  const toggleOption = (optionId: string): void => {
    if (!user?.sub || !trip || !focusCard) return;
    const cityId = focusCard.cityId;
    const list = (trip.cityOptions?.[cityId] ?? []).map((o) =>
      o.id === optionId ? { ...o, completed: !o.completed } : o
    );
    const updated = updateTrip(user.sub, trip.id, {
      cityOptions: { ...(trip.cityOptions ?? {}), [cityId]: list },
    });
    if (updated) setTrip(updated);
  };

  const goAddItem = (): void => {
    if (!tripId || !dayDate) {
      message.info(dayViewEn.needDate);
      return;
    }
    if (searchCities.length === 0) {
      message.info(dayViewEn.needDate);
      return;
    }
    setPlaceSearchOpen(true);
  };

  const closeItemModal = (): void => {
    setAddItemOpen(false);
    setAddItemPrefill(null);
    setEditingItem(null);
    setItemModalMode('add');
  };

  const openAddForm = (prefill: AddDayItemPrefill | null): void => {
    setPlaceSearchOpen(false);
    setItemModalMode('add');
    setEditingItem(null);
    setAddItemPrefill(prefill);
    setAddItemOpen(true);
  };

  const openEditItem = (item: ItineraryItem): void => {
    setPlaceSearchOpen(false);
    setItemModalMode('edit');
    setEditingItem(item);
    setAddItemPrefill(prefillFromItem(item));
    setAddItemOpen(true);
  };

  const handlePlaceSelected = (selection: PlaceSearchSelection): void => {
    openAddForm(prefillFromPlace(selection.place, selection.suggestedType));
  };

  const handleManualEntry = (): void => {
    openAddForm({ type: 'activity' });
  };

  const handleTourAdd = (payload: PlaceSearchTourAdd): void => {
    if (!user?.sub || !trip) return;
    setTourAdding(true);
    try {
      const item = {
        ...buildTourItineraryItem({
          tour: payload.tour,
          dateYmd: payload.dateYmd,
          slot: payload.slot,
          cityName: payload.city.cityName,
        }),
        id: crypto.randomUUID(),
      };
      const days = [...(trip.itineraryDays ?? [])];
      const dayIndex = days.findIndex((d) => d.date === payload.dateYmd);
      if (dayIndex >= 0) {
        const day = days[dayIndex];
        days[dayIndex] = {
          ...day,
          items: [...(day.items ?? []), item],
          cityId: day.cityId ?? payload.city.id,
        };
      } else {
        days.push({
          date: payload.dateYmd,
          items: [item],
          cityId: payload.city.id,
        });
      }
      const updated = updateTrip(user.sub, trip.id, { itineraryDays: days });
      if (updated) {
        setTrip(updated);
        message.success(dayViewEn.itemSaved);
        setPlaceSearchOpen(false);
      } else {
        message.error(tourSearchEn.saveFailed);
      }
    } catch {
      message.error(tourSearchEn.saveFailed);
    } finally {
      setTourAdding(false);
    }
  };

  const persistDayItems = (
    nextItems: ItineraryItem[],
    extra?: Partial<Omit<Trip, 'id'>>
  ): Trip | null => {
    if (!user?.sub || !trip || !dayDate) return null;
    const days = [...(trip.itineraryDays ?? [])];
    const dayIndex = days.findIndex((d) => d.date === dayDate);
    if (dayIndex >= 0) {
      const day = days[dayIndex];
      days[dayIndex] = {
        ...day,
        items: nextItems,
        cityId: day.cityId ?? focusCard?.cityId,
      };
    } else {
      days.push({
        date: dayDate,
        items: nextItems,
        cityId: focusCard?.cityId,
      });
    }
    return updateTrip(user.sub, trip.id, { itineraryDays: days, ...extra });
  };

  const handleSaveItem = (payload: AddDayItemSavePayload): void => {
    if (!user?.sub || !trip || !dayDate) return;
    const { item, attachmentRefs: newRefs } = payload;

    if (itemModalMode === 'edit' && item.id) {
      const nextItems = dayItemsAll.map((existing) =>
        existing.id === item.id ? ({ ...existing, ...item, id: item.id } as ItineraryItem) : existing
      );
      const withDays = persistDayItems(nextItems);
      if (!withDays) return;
      const retainedRefs = (withDays.attachmentRefs ?? []).filter((r) =>
        (item.attachments ?? []).some((a) => a.id === r.attachmentId)
      );
      const mergedRefs = [...retainedRefs, ...newRefs].filter(
        (r, idx, arr) => arr.findIndex((x) => x.attachmentId === r.attachmentId) === idx
      );
      const next = applyItemAttachmentsOnSave(withDays, {
        itemId: item.id,
        attachments: item.attachments ?? [],
        refs: mergedRefs,
      });
      const updated = updateTrip(user.sub, trip.id, {
        itineraryDays: next.itineraryDays,
        attachmentRefs: next.attachmentRefs,
        attachmentFileIds: next.attachmentFileIds,
      });
      if (updated) {
        setTrip(updated);
        message.success(dayViewEn.itemUpdated);
      }
      closeItemModal();
      return;
    }

    const newItem: ItineraryItem = { ...item, id: crypto.randomUUID() };
    const withDays = persistDayItems([...dayItemsAll, newItem]);
    if (!withDays) return;
    const next = applyItemAttachmentsOnSave(withDays, {
      itemId: newItem.id,
      attachments: item.attachments ?? [],
      refs: newRefs,
    });
    const updated = updateTrip(user.sub, trip.id, {
      itineraryDays: next.itineraryDays,
      attachmentRefs: next.attachmentRefs,
      attachmentFileIds: next.attachmentFileIds,
    });
    if (updated) {
      setTrip(updated);
      message.success(dayViewEn.itemSaved);
    }
    closeItemModal();
  };

  const handleDeleteItem = (): void => {
    if (!editingItem?.id || !user?.sub || !trip) return;
    const nextItems = dayItemsAll.filter((i) => i.id !== editingItem.id);
    let next: Trip = {
      ...trip,
      itineraryDays: (trip.itineraryDays ?? []).map((d) =>
        d.date === dayDate ? { ...d, items: nextItems } : d
      ),
    };
    for (const a of editingItem.attachments ?? []) {
      next = removeAttachment(next, a.id);
    }
    const updated = updateTrip(user.sub, trip.id, {
      itineraryDays: next.itineraryDays,
      attachmentRefs: next.attachmentRefs,
      attachmentFileIds: next.attachmentFileIds,
    });
    if (updated) {
      setTrip(updated);
      message.success(dayViewEn.itemDeleted);
    }
    closeItemModal();
  };

  if (loading || isDataSyncing) {
    return (
      <div className={styles.dayView}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (!trip || !dayDate) {
    return (
      <div className={styles.dayView}>
        <div className={styles.container}>
          <p>{dayViewEn.notFound}</p>
          <Button type="primary" onClick={() => navigate(tripId ? `/trips/${tripId}` : '/trips')}>
            {tripDetailEn.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dayView}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Link to={`/trips/${trip.id}`} className={styles.backLink}>
            <ArrowLeftOutlined /> {dayViewEn.back}
          </Link>
          <h1 className={styles.navTitle}>{navTitle}</h1>
        </div>

        <div className={styles.stack}>
          {/* Weather / city header — mirrors iOS weatherDrawerSection */}
          <div className={styles.weatherHeroWrap}>
            <div
              className={`${styles.weatherHero} ${styles[heroVariant]} ${cityPhotoUrl ? styles.weatherHeroWithPhoto : ''}`}
              style={cityPhotoUrl ? { backgroundImage: `url(${cityPhotoUrl})` } : undefined}
              aria-hidden
            />
            <button
              type="button"
              className={styles.weatherHeader}
              onClick={() => setWeatherExpanded((v) => !v)}
            >
              <div className={styles.weatherTop}>
                <div className={styles.weatherText}>
                  <div className={styles.weatherDate}>{weekdayLabel}</div>
                  <div className={styles.weatherCityRow}>
                    <span className={styles.weatherCity}>{cityName}</span>
                    {!atSea ? (
                      <button
                        type="button"
                        className={styles.infoIcon}
                        aria-label={dayViewEn.countryInfo ?? 'Country information'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCountryInfoOpen(true);
                        }}
                      >
                        ⓘ
                      </button>
                    ) : null}
                    {statusLabel ? (
                      <span
                        className={`${styles.statusBadge} ${
                          focusCard?.cruisePortStatus === 'tendered'
                            ? styles.statusTendered
                            : focusCard?.cruisePortStatus === 'cruising'
                              ? styles.statusCruising
                              : styles.statusDocked
                        }`}
                      >
                        <FerryIcon size={14} />
                        {statusLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className={styles.weatherTemps}>
                  {weatherStatus === 'loading' ? (
                    <span className={styles.weatherTempsMuted}>{weatherEn.loading}</span>
                  ) : weatherStatus === 'ok' && weatherDay ? (
                    <>
                      <WeatherConditionIcon
                        iconName={weatherDay.iconName}
                        dimmed={weatherDay.isHistorical}
                        size={20}
                      />
                      <span className={styles.weatherTempRange}>
                        {formatTempRange(weatherDay.highTempC, weatherDay.lowTempC, temperatureUnit)}
                      </span>
                    </>
                  ) : (
                    <span className={styles.weatherTempsMuted}>{weatherEn.unavailable}</span>
                  )}
                </div>
                <span className={styles.expandChevron} aria-hidden>
                  {weatherExpanded ? <UpOutlined /> : <DownOutlined />}
                </span>
              </div>
              {weatherExpanded ? (
                <div className={styles.weatherExpanded}>
                  {timeLine ? <div className={styles.portTimes}>{timeLine}</div> : null}
                  {focusCard?.portArrivalHm ? (
                    <div className={styles.portDetail}>
                      {dayViewEn.arrival}: {formatPortClockHm(focusCard.portArrivalHm)}
                    </div>
                  ) : null}
                  {focusCard?.portDepartureHm ? (
                    <div className={styles.portDetail}>
                      {dayViewEn.departure}: {formatPortClockHm(focusCard.portDepartureHm)}
                    </div>
                  ) : null}
                  {!timeLine && !focusCard?.portArrivalHm && !focusCard?.portDepartureHm ? (
                    <div className={styles.portDetail}>{dayViewEn.noPortTimes}</div>
                  ) : null}

                  {!atSea ? (
                    <div className={styles.weatherMeta}>
                      <DayWeatherSummary
                        day={weatherDay}
                        unit={temperatureUnit}
                        status={weatherStatus}
                        showHistoricalBadge
                      />
                      {showHourlyStrip ? (
                        <HourlyWeatherStrip hours={weatherHourly} unit={temperatureUnit} />
                      ) : null}
                      {weatherLastUpdated ? (
                        <div className={styles.weatherUpdated}>
                          {weatherEn.lastUpdated}:{' '}
                          {new Date(weatherLastUpdated).toLocaleString(undefined, {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </div>
                      ) : null}
                      <div className={styles.weatherAttribution}>{weatherEn.attribution}</div>
                      {weatherAlerts.length > 0 ? (
                        <button
                          type="button"
                          className={styles.alertsBadge}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAlertsDrawerOpen(true);
                          }}
                        >
                          <WarningOutlined />
                          {weatherEn.alertsTitle} ({weatherAlerts.length})
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </button>
          </div>

          {/* Hotel / accommodation cards — mirrors iOS HotelCardView */}
          {accommodations.map((hotel) => (
            <HotelCard
              key={hotel.id}
              hotel={hotel}
              dayDate={dayDate}
              expanded={expandAll}
              preferredMapApp={preferredMapApp}
              onEdit={openEditItem}
            />
          ))}

          {/* Activity items — mirrors iOS ItemCardView */}
          {dayItems.length > 0 ? (
            dayItems.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                trip={trip}
                dayDate={dayDate}
                expanded={expandAll}
                collapsePastItems={collapsePastItems}
                preferredMapApp={preferredMapApp}
                onEdit={openEditItem}
              />
            ))
          ) : (
            <button type="button" className={styles.emptyActivities} onClick={goAddItem}>
              <div className={styles.emptyTitle}>{dayViewEn.activities}</div>
              <div className={styles.emptyCta}>{dayViewEn.addMealsActivities}</div>
            </button>
          )}

          {/* Options — Things I May Do */}
          <div className={styles.optionsCard}>
            <div className={styles.optionsHeader}>{dayViewEn.options}</div>
            {options.length === 0 ? (
              <p className={styles.muted}>{dayViewEn.noOptions}</p>
            ) : (
              <ul className={styles.optionsList}>
                {options.map((opt) => (
                  <li key={opt.id} className={styles.optionRow}>
                    <button
                      type="button"
                      className={`${styles.optionCheck} ${opt.completed ? styles.optionDone : ''}`}
                      onClick={() => toggleOption(opt.id)}
                      aria-pressed={opt.completed}
                    >
                      {opt.completed ? '✓' : ''}
                    </button>
                    <div className={styles.optionBody}>
                      <div
                        className={`${styles.optionTitle} ${opt.completed ? styles.optionTitleDone : ''}`}
                      >
                        {opt.title}
                      </div>
                      {opt.description ? (
                        <div className={styles.optionDesc}>{opt.description}</div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <NotesAttachmentsCard
            trip={trip}
            dayDate={dayDate}
            cityVisitId={focusCard?.cityId}
            defaultExpanded
            expandAll={expandAll}
            onChangeTrip={(updates) => {
              if (!user?.sub || !trip) return;
              const updated = updateTrip(user.sub, trip.id, updates);
              if (updated) setTrip(updated);
            }}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <Button type="primary" className={styles.addItemBtn} icon={<PlusOutlined />} onClick={goAddItem}>
            {dayViewEn.addItem}
          </Button>
          <Button
            className={styles.expandBtn}
            icon={expandAll ? <UpOutlined /> : <DownOutlined />}
            onClick={() => {
              setExpandAll((v) => {
                const next = !v;
                patchAppUserSettings({ expandAllItemsByDefault: next });
                return next;
              });
            }}
          >
            {expandAll ? dayViewEn.collapse : dayViewEn.expand}
          </Button>
        </div>
      </div>

      <PlaceSearchModal
        open={placeSearchOpen}
        cities={searchCities}
        initialCityId={focusCard?.cityId}
        enableTours={Boolean(focusCard?.cityId && !atSea)}
        userId={user?.sub}
        arrivalDate={focusCard?.arrivalDate}
        departureDate={focusCard?.departureDate}
        initialTourDate={dayDate}
        tourAdding={tourAdding}
        onClose={() => setPlaceSearchOpen(false)}
        onPlaceSelected={handlePlaceSelected}
        onManualEntry={handleManualEntry}
        onTourAdd={handleTourAdd}
      />
      <AddDayItemModal
        open={addItemOpen}
        mode={itemModalMode}
        editingItem={editingItem}
        prefill={addItemPrefill}
        onClose={closeItemModal}
        onSave={handleSaveItem}
        onDelete={itemModalMode === 'edit' ? handleDeleteItem : undefined}
      />
      <WeatherAlertsDrawer
        open={alertsDrawerOpen}
        alerts={weatherAlerts}
        onClose={() => setAlertsDrawerOpen(false)}
      />
      <CountryInfoDrawer
        open={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
        cityName={cityName}
        countryISO2={focusCard?.countryISO2}
        countryName={focusCard?.countryName}
        currencyCode={focusCard?.currencyCode}
        timeZoneIdentifier={focusCard?.timeZoneIdentifier}
        tripStartDate={trip?.startDate}
        cityArrivalDate={focusCard?.arrivalDate}
        tripCurrency={trip?.currency}
        tripHomeTimeZone={trip?.homeTimeZone}
        isCruisePort={Boolean(focusCard?.isCruisePort)}
        coords={
          focusCard?.cityId && trip?.cityCoords?.[focusCard.cityId]
            ? trip.cityCoords[focusCard.cityId]
            : null
        }
        dockNumber={focusCard?.dockNumber}
        portArrivalHm={focusCard?.portArrivalHm}
        portDepartureHm={focusCard?.portDepartureHm}
      />
    </div>
  );
};

const ItemCard: React.FC<{
  item: ItineraryItem;
  trip: Trip;
  dayDate: string | null;
  expanded: boolean;
  collapsePastItems: boolean;
  preferredMapApp: string;
  onEdit: (item: ItineraryItem) => void;
}> = ({ item, trip, dayDate, expanded, collapsePastItems, preferredMapApp, onEdit }) => {
  const [open, setOpen] = useState(() => {
    if (expanded) return true;
    if (!collapsePastItems) return false;
    return isItemStillUpcoming(dayDate, item.time);
  });
  const show = expanded || open;
  const typeClass =
    item.type === 'meal'
      ? styles.typeMeal
      : item.type === 'activity'
        ? styles.typeActivity
        : item.type === 'transport'
          ? styles.typeTransport
          : item.type === 'accommodation'
            ? styles.typeAccommodation
            : styles.typeOther;

  const iconClass =
    item.type === 'meal'
      ? styles.iconMeal
      : item.type === 'activity'
        ? styles.iconActivity
        : item.type === 'transport'
          ? styles.iconTransport
          : item.type === 'accommodation'
            ? styles.iconAccommodation
            : styles.iconOther;

  const displayAddress = item.location?.address ?? item.address;
  const mapHref = mapsUrlForItem(item, preferredMapApp);
  const hasAttachments = Boolean(item.attachments?.length);
  const hasTextDetails = Boolean(
    item.notes ||
      item.confirmationNumber ||
      item.durationMinutes ||
      displayAddress ||
      item.phone ||
      item.url ||
      item.barcode?.payload
  );

  return (
    <div className={`${styles.itemCard} ${typeClass}`}>
      <div className={styles.itemHeader}>
        <button
          type="button"
          className={styles.itemHeaderMain}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={show}
        >
          <span className={`${styles.typeIcon} ${iconClass}`} aria-hidden>
            <TypeIcon type={item.type} size={22} />
          </span>
          {item.time ? <span className={styles.itemTime}>{item.time}</span> : null}
          <span className={styles.itemTitle}>{item.title}</span>
          <span className={styles.itemHeaderSpacer} />
        </button>
        {mapHref ? (
          <a
            className={styles.mapBtn}
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            title={dayViewEn.openMap}
            aria-label={dayViewEn.openMap}
          >
            <MapIcon />
          </a>
        ) : null}
        <button
          type="button"
          className={styles.editBtn}
          onClick={() => onEdit(item)}
          aria-label={dayViewEn.editItem}
          title={dayViewEn.editItem}
        >
          <EditOutlined />
        </button>
        <button
          type="button"
          className={styles.itemChevronBtn}
          onClick={() => setOpen((v) => !v)}
          aria-label={show ? dayViewEn.collapse : dayViewEn.expand}
        >
          {show ? <UpOutlined /> : <DownOutlined />}
        </button>
      </div>
      {show ? (
        <div className={styles.itemBody}>
          {hasAttachments ? <ItemAttachmentStrip trip={trip} item={item} /> : null}
          {hasTextDetails ? (
            item.type === 'meal' ? (
              <MealBody item={item} displayAddress={displayAddress} />
            ) : item.type === 'activity' ? (
              <ActivityBody item={item} displayAddress={displayAddress} />
            ) : item.type === 'transport' ? (
              <TransportBody item={item} displayAddress={displayAddress} />
            ) : (
              <GenericBody item={item} displayAddress={displayAddress} />
            )
          ) : !hasAttachments ? (
            <button type="button" className={styles.editHint} onClick={() => onEdit(item)}>
              {dayViewEn.editItem}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ItemAttachmentStrip: React.FC<{ trip: Trip; item: ItineraryItem }> = ({ trip, item }) => {
  const openAttachment = async (attachmentId: string): Promise<void> => {
    const fileId = fileIdForAttachment(trip, attachmentId);
    if (!fileId) {
      message.error(dayViewEn.attachmentDownloadFailed);
      return;
    }
    try {
      const blob = await downloadFile(fileId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      message.error(dayViewEn.attachmentDownloadFailed);
    }
  };

  return (
    <div className={styles.attachmentStrip} data-testid="item-attachment-strip">
      {(item.attachments ?? []).map((a) => (
        <ItemAttachmentThumb
          key={a.id}
          trip={trip}
          attachmentId={a.id}
          fileName={a.fileName}
          mimeType={a.mimeType}
          onOpen={() => void openAttachment(a.id)}
        />
      ))}
    </div>
  );
};

const ItemAttachmentThumb: React.FC<{
  trip: Trip;
  attachmentId: string;
  fileName: string;
  mimeType: string;
  onOpen: () => void;
}> = ({ trip, attachmentId, fileName, mimeType, onOpen }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = mimeType.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    const fileId = fileIdForAttachment(trip, attachmentId);
    if (!fileId) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void downloadFile(fileId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        /* icon fallback */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [trip, attachmentId, isImage]);

  const shortName = fileName.length > 12 ? `${fileName.slice(0, 11)}…` : fileName;

  return (
    <button
      type="button"
      className={styles.attachmentThumb}
      onClick={onOpen}
      title={fileName}
    >
      {previewUrl ? (
        <img src={previewUrl} alt="" className={styles.attachmentThumbImage} />
      ) : isImage ? (
        <PictureOutlined className={styles.attachmentThumbIcon} />
      ) : (
        <FileOutlined className={styles.attachmentThumbIcon} />
      )}
      <span className={styles.attachmentThumbName}>{shortName}</span>
    </button>
  );
};

const HotelCard: React.FC<{
  hotel: ItineraryItem;
  dayDate: string | null;
  expanded: boolean;
  preferredMapApp: string;
  onEdit: (item: ItineraryItem) => void;
}> = ({ hotel, dayDate, expanded, preferredMapApp, onEdit }) => {
  const [open, setOpen] = useState(expanded);
  const show = expanded || open;
  const isCheckIn = hotel.accommodationCheckInDate === dayDate;
  const isCheckOut = hotel.accommodationCheckOutDate === dayDate;
  const statusLabel =
    isCheckIn && isCheckOut
      ? `${dayViewEn.checkIn} & ${dayViewEn.checkOut}`
      : isCheckIn
        ? dayViewEn.checkIn
        : isCheckOut
          ? dayViewEn.checkOut
          : dayViewEn.tonight;
  const statusClass =
    isCheckIn && !isCheckOut
      ? styles.hotelStatusCheckIn
      : isCheckOut
        ? styles.hotelStatusCheckOut
        : styles.hotelStatusTonight;
  const mapHref = mapsUrlForItem(hotel, preferredMapApp);
  const hasBody = Boolean(hotel.confirmationNumber || hotel.phone || hotel.notes || hotel.location?.address);

  return (
    <div className={`${styles.itemCard} ${styles.typeAccommodation}`}>
      <div className={styles.itemHeader}>
        <button
          type="button"
          className={styles.itemHeaderMain}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={show}
        >
          <span className={`${styles.typeIcon} ${styles.iconAccommodation}`} aria-hidden>
            <AccommodationIcon size={22} />
          </span>
          <span className={styles.itemTitle}>{hotel.title || dayViewEn.hotel}</span>
          <span className={styles.itemHeaderSpacer} />
        </button>
        {mapHref ? (
          <a
            className={styles.mapBtn}
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            title={dayViewEn.openMap}
            aria-label={dayViewEn.openMap}
          >
            <MapIcon />
          </a>
        ) : null}
        <span className={`${styles.hotelStatusChip} ${statusClass}`}>{statusLabel}</span>
        <button
          type="button"
          className={styles.editBtn}
          onClick={() => onEdit(hotel)}
          aria-label={dayViewEn.editItem}
          title={dayViewEn.editItem}
        >
          <EditOutlined />
        </button>
        <button
          type="button"
          className={styles.itemChevronBtn}
          onClick={() => setOpen((v) => !v)}
          aria-label={show ? dayViewEn.collapse : dayViewEn.expand}
        >
          {show ? <UpOutlined /> : <DownOutlined />}
        </button>
      </div>
      {show && hasBody ? (
        <div className={styles.itemBody}>
          {hotel.confirmationNumber ? (
            <div className={styles.detailRow}>
              <span className={styles.detailIconGreen} aria-hidden>
                <CheckIcon />
              </span>
              <span className={styles.detailValue}>
                {dayViewEn.conf} {hotel.confirmationNumber}
              </span>
            </div>
          ) : null}
          {hotel.phone ? (
            <div className={styles.detailRow}>
              <span className={styles.detailIconMuted} aria-hidden>
                <PhoneIcon />
              </span>
              <a className={styles.detailLink} href={`tel:${hotel.phone}`}>
                {hotel.phone}
              </a>
            </div>
          ) : null}
          {hotel.notes ? <div className={styles.itemNotes}>{hotel.notes}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

function mapsUrlForItem(
  item: ItineraryItem,
  preferredMapApp?: string | null
): string | null {
  const loc = item.location;
  return mapsSearchUrl({
    preferredMapApp,
    latitude: loc?.latitude,
    longitude: loc?.longitude,
    query: loc?.address ?? item.address,
    name: item.title,
  });
}


const MealBody: React.FC<{ item: ItineraryItem; displayAddress?: string }> = ({
  item,
  displayAddress,
}) => (
  <>
    <div className={styles.detailRow}>
      <span className={styles.detailIconOrange} aria-hidden>
        <TypeIcon type="meal" size={22} />
      </span>
      <span className={styles.detailValue}>
        {item.title}
        {item.confirmationNumber ? ` · ${dayViewEn.conf} ${item.confirmationNumber}` : ''}
      </span>
    </div>
    {displayAddress ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconRed} aria-hidden>
          <PinIcon />
        </span>
        <span className={styles.detailValue}>{displayAddress}</span>
      </div>
    ) : null}
    {item.url ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconBlue} aria-hidden>
          <GlobeIcon />
        </span>
        <a
          className={styles.detailLink}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {dayViewEn.website}
        </a>
      </div>
    ) : null}
    {item.phone ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconGreen} aria-hidden>
          <PhoneIcon />
        </span>
        <a className={styles.detailLink} href={`tel:${item.phone}`}>
          {item.phone}
        </a>
      </div>
    ) : null}
    {item.notes ? <div className={styles.itemNotes}>{item.notes}</div> : null}
  </>
);

const ActivityBody: React.FC<{ item: ItineraryItem; displayAddress?: string }> = ({
  item,
  displayAddress,
}) => (
  <>
    {item.confirmationNumber ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconGreen} aria-hidden>
          <CheckIcon />
        </span>
        <span className={styles.detailValue}>
          {dayViewEn.conf} {item.confirmationNumber}
        </span>
      </div>
    ) : null}
    {displayAddress ? (
      <div className={styles.detailBlock}>
        <div className={styles.detailLabel}>{dayViewEn.location}</div>
        <div className={styles.detailRow}>
          <span className={styles.detailIconOrange} aria-hidden>
            <PinIcon />
          </span>
          <span className={styles.detailValue}>{displayAddress}</span>
        </div>
      </div>
    ) : null}
    {item.durationMinutes != null ? (
      <div className={styles.detailBlock}>
        <div className={styles.detailLabel}>{dayViewEn.duration}</div>
        <div className={styles.detailRow}>
          <span className={styles.detailIconBlue} aria-hidden>
            <ClockIcon />
          </span>
          <span className={styles.detailValue}>{formatDurationMinutes(item.durationMinutes)}</span>
        </div>
      </div>
    ) : null}
    {item.url ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconBlue} aria-hidden>
          <GlobeIcon />
        </span>
        <a
          className={styles.detailLink}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {dayViewEn.website}
        </a>
      </div>
    ) : null}
    {item.phone ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconGreen} aria-hidden>
          <PhoneIcon />
        </span>
        <a className={styles.detailLink} href={`tel:${item.phone}`}>
          {item.phone}
        </a>
      </div>
    ) : null}
    {item.notes ? <div className={styles.itemNotes}>{item.notes}</div> : null}
    {item.barcode?.payload ? (
      <div className={styles.detailRow}>
        <span className={styles.detailValue}>
          {dayViewEn.barcode} {item.barcode.type ? `${item.barcode.type} · ` : ''}
          {item.barcode.payload}
        </span>
      </div>
    ) : null}
  </>
);

const TransportBody: React.FC<{ item: ItineraryItem; displayAddress?: string }> = ({
  item,
  displayAddress,
}) => <GenericBody item={item} displayAddress={displayAddress} />;

const GenericBody: React.FC<{ item: ItineraryItem; displayAddress?: string }> = ({
  item,
  displayAddress,
}) => (
  <>
    {item.confirmationNumber ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconGreen} aria-hidden>
          <CheckIcon />
        </span>
        <span className={styles.detailValue}>
          {dayViewEn.conf} {item.confirmationNumber}
        </span>
      </div>
    ) : null}
    {displayAddress ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconRed} aria-hidden>
          <PinIcon />
        </span>
        <span className={styles.detailValue}>{displayAddress}</span>
      </div>
    ) : null}
    {item.durationMinutes != null ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconBlue} aria-hidden>
          <ClockIcon />
        </span>
        <span className={styles.detailValue}>{formatDurationMinutes(item.durationMinutes)}</span>
      </div>
    ) : null}
    {item.phone ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconGreen} aria-hidden>
          <PhoneIcon />
        </span>
        <a className={styles.detailLink} href={`tel:${item.phone}`}>
          {item.phone}
        </a>
      </div>
    ) : null}
    {item.url ? (
      <div className={styles.detailRow}>
        <span className={styles.detailIconBlue} aria-hidden>
          <GlobeIcon />
        </span>
        <a
          className={styles.detailLink}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {dayViewEn.website}
        </a>
      </div>
    ) : null}
    {item.notes ? <div className={styles.itemNotes}>{item.notes}</div> : null}
    {item.barcode?.payload ? (
      <div className={styles.detailRow}>
        <span className={styles.detailValue}>
          {dayViewEn.barcode} {item.barcode.type ? `${item.barcode.type} · ` : ''}
          {item.barcode.payload}
        </span>
      </div>
    ) : null}
  </>
);

export default DayView;
